import axios from 'axios';

// Interface definitions matching doit-rules API
export interface Rule {
    id?: number;
    ruleName: string;
    nameSlug?: string;
    description?: string;
    filePath?: string;
    createdAt?: string;
    updatedAt?: string;
    domain?: string;
    invoiceType?: string;
    version?: string;
    conditions?: RuleCondition[];
    actions?: RuleAction[];
}

export interface RuleCondition {
    id?: number;
    type?: string;
    field: string;
    operator: string;
    value: string;
    conditions?: string; // "AND" or "OR"
}

export interface RuleAction {
    id?: number;
    actionType: string;
    field?: string;
    type?: string;
    operation?: string;
    value?: string;
    delimiter?: string;
    toField?: string;
    toSection?: string;
    indexValue?: number;
}

export interface RuleListResponse {
    content: Rule[];
    totalPages: number;
    totalElements: number;
    size: number;
    number: number;
}

// Get auth token from localStorage
const getAuthToken = (): string => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create API instance pointing to doit-rules service
const rulesApi = axios.create({
    baseURL: '/rules',
});

// Add request interceptor for auth token
rulesApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
rulesApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Unauthorized - token may have expired');
        }
        return Promise.reject(error);
    }
);

// ============================================================================
// Rule CRUD Operations
// ============================================================================

/**
 * Get paginated list of rules
 */
export const getRules = async (
    page: number = 0,
    size: number = 20,
    search?: string
): Promise<RuleListResponse> => {
    const params: any = { page, size };
    if (search) {
        params.search = search;
    }
    const response = await rulesApi.get<RuleListResponse>("/api/rule/paged", { params });
    return response.data;
};

/**
 * Get rule by ID
 */
export const getRule = async (id: number): Promise<Rule> => {
    const response = await rulesApi.get<Rule>(`/api/rule/${id}`);
    return response.data;
};

/**
 * Create new rule
 */
export const createRule = async (rule: Partial<Rule>): Promise<Rule> => {
    const response = await rulesApi.post<Rule>("/api/rule", rule);
    return response.data;
};

/**
 * Update rule
 */
export const updateRule = async (id: number, rule: Partial<Rule>): Promise<Rule> => {
    const response = await rulesApi.put<Rule>(`/api/rule/${id}`, rule);
    return response.data;
};

/**
 * Delete rule
 */
export const deleteRule = async (id: number): Promise<void> => {
    await rulesApi.delete(`/api/rule/${id}`);
};

/**
 * Execute rule
 */
export const executeRule = async (
    id: number,
    input: Record<string, any>
): Promise<Record<string, any>> => {
    const response = await rulesApi.post<Record<string, any>>(`/api/rule/${id}/execute`, input);
    return response.data;
};

/**
 * Get all rules (non-paginated)
 */
export const getAllRules = async (): Promise<Rule[]> => {
    const response = await rulesApi.get<Rule[]>("/api/rule");
    return response.data;
};
